import { spawn } from 'node:child_process'
import type {
  RailwayRunMode,
  RailwayRunner,
  RunRailwayResult
} from '@cli-types/next-adapter/railway.js'
import { DEFAULT_TIMEOUT_MS } from '@cli-constants/timeouts.js'
import { stripAnsi } from '@cli-utils/text.js'

export function runRailway(
  runner: RailwayRunner,
  args: string[],
  options: {
    cwd: string
    mode: RailwayRunMode
    timeoutMs?: number
    env?: NodeJS.ProcessEnv
    stdinInput?: string
    onOutputLine?: (line: string) => void
  }
): Promise<RunRailwayResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const argv = [...runner.prefix, ...args]

  return new Promise((resolve) => {
    const child = spawn(runner.bin, argv, {
      cwd: options.cwd,
      stdio: options.mode === 'inherit' ? 'inherit' : 'pipe',
      env: { ...process.env, npm_config_loglevel: 'error', ...options.env }
    })

    let stdout = ''
    let stderr = ''
    let timedOut = false
    let stdoutPending = ''
    let stderrPending = ''
    let settled = false
    let forceKillTimer: NodeJS.Timeout | undefined

    const observe = (pending: string, chunk: string): string => {
      if (!options.onOutputLine) return pending
      const parts = `${pending}${chunk}`.split(/\r\n|\n|\r/)
      const nextPending = parts.pop() ?? ''
      for (const part of parts) {
        const line = stripAnsi(part).trim()
        if (line) options.onOutputLine(line)
      }
      return nextPending
    }

    if (options.mode === 'capture') {
      if (options.stdinInput !== undefined) {
        child.stdin?.write(options.stdinInput)
      }
      child.stdin?.end()

      child.stdout?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stdout += text
        stdoutPending = observe(stdoutPending, text)
      })
      child.stderr?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stderr += text
        stderrPending = observe(stderrPending, text)
      })
    }

    const timeout = setTimeout(() => {
      timedOut = true
      child.kill('SIGTERM')
      forceKillTimer = setTimeout(() => child.kill('SIGKILL'), 2_000)
    }, timeoutMs)

    const finish = (code: number | null, signal: NodeJS.Signals | null, errorMessage?: string) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      if (forceKillTimer) clearTimeout(forceKillTimer)
      for (const pending of [stdoutPending, stderrPending]) {
        const line = stripAnsi(pending).trim()
        if (line) options.onOutputLine?.(line)
      }
      resolve({
        success: !timedOut && !errorMessage && code === 0,
        code,
        signal,
        stdout,
        stderr,
        timedOut,
        errorMessage:
          errorMessage ??
          (timedOut
            ? `Railway command timed out after ${Math.floor(timeoutMs / 1000)}s`
            : code === 0
              ? undefined
              : signal
                ? `Railway command terminated by ${signal}`
                : `Railway command exited with code ${code ?? 'unknown'}`)
      })
    }

    child.on('close', (code, signal) => finish(code, signal))
    child.on('error', (error) => finish(null, null, error.message))
  })
}
